import { JSX } from "react";
import { motion } from "motion/react";
import { HeartIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { CheckCircleIcon } from "@heroicons/react/24/outline";

const styles = {
    section: "bg-gradient-to-b from-green-50 via-white to-green-50/30 py-20",
    container: "mx-auto max-w-7xl px-6 lg:px-8",
    header: "text-center mb-12",
    title: "text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-4",
    subtitle: "mt-4 text-xl text-gray-600 max-w-2xl mx-auto",
    badge: "inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6",
    badgeIcon: "h-4 w-4",
    grid: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6",
    card: "group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2",
    cardImageWrap: "relative h-52 overflow-hidden bg-gray-100",
    cardImage: "w-full h-full object-cover transition-transform duration-500 group-hover:scale-105",
    cardOverlay: "absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300",
    cardTag: "absolute top-3 left-3 px-2.5 py-1 bg-white/95 backdrop-blur-sm rounded-full text-xs font-semibold text-green-700 flex items-center gap-1 shadow-sm",
    cardContent: "p-5",
    cardTitle: "text-lg font-bold text-gray-900 mb-1",
    cardBreed: "text-xs text-gray-400 uppercase tracking-wide mb-2",
    cardStory: "text-gray-600 text-sm leading-relaxed line-clamp-2",
    cardFooter: "flex items-center gap-2 mt-3 pt-3 border-t border-gray-100",
    footerIcon: "h-4 w-4 text-green-600 flex-shrink-0",
    footerText: "text-xs text-gray-500",
};

const pets = [
    {
        id: 1,
        name: "Luna",
        breed: "Golden Retriever",
        image: "https://images.unsplash.com/photo-1552053831-71594a27632d?w=400&q=80",
        tag: "Vacunas al día",
        story: "Gracias a los recordatorios automáticos, Luna nunca más perdió una vacuna. Su dueño está tranquilo.",
    },
    {
        id: 2,
        name: "Max",
        breed: "Beagle",
        image: "https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=400&q=80",
        tag: "Alergia detectada",
        story: "El historial digital permitió identificar rápidamente una alergia alimentaria en Max.",
    },
    {
        id: 3,
        name: "Mía",
        breed: "Gata Persia",
        image: "https://images.unsplash.com/photo-1574158622682-e40e69881006?w=400&q=80",
        tag: "Diabetes controlada",
        story: "La agenda inteligente coordinó el tratamiento de diabetes de Mía sin un solo error.",
    },
    {
        id: 4,
        name: "Rocky",
        breed: "Pastor Alemán",
        image: "https://images.unsplash.com/photo-1589941013453-ec89f33b5e95?w=400&q=80",
        tag: "Emergencia resuelta",
        story: "Rocky llegó herido. En segundos, el equipo accesó sus datos y empezó el tratamiento.",
    },
    {
        id: 5,
        name: "Nube",
        breed: "Siamés",
        image: "https://images.unsplash.com/photo-1592194996308-7b43878e84a6?w=400&q=80",
        tag: "Chequeo preventivo",
        story: "El chequeo automático detectó anomalías en Nube antes de que su dueño lo notara.",
    },
    {
        id: 6,
        name: "Thor",
        breed: "Husky",
        image: "https://images.unsplash.com/photo-1568572933382-74d440642117?w=400&q=80",
        tag: "Primeros auxilios",
        story: "La app guió al dueño de Thor para dar primeros auxilios mientras llegaba a la clínica.",
    },
    {
        id: 7,
        name: "Bella",
        breed: "Labrador",
        image: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&q=80",
        tag: "Rehabilitación",
        story: "El seguimiento preciso del tratamiento hizo que Bella completara su rehabilitación 2 semanas antes.",
    },
    {
        id: 8,
        name: "Simba",
        breed: "Maine Coon",
        image: "https://images.unsplash.com/photo-1548546738-8509cb246ed3?w=400&q=80",
        tag: "Nutrición",
        story: "La dieta personalizada basada en su historial mejoró el pelaje y energía de Simba.",
    },
];

const PetStoriesSection = (): JSX.Element => (
    <section className={styles.section}>
        <div className={styles.container}>
            <div className={styles.header}>
                <span className={styles.badge}>
                    <HeartIcon className={styles.badgeIcon} />
                    Historias reales
                </span>
                <h2 className={styles.title}>
                    Historias que Inspiran
                </h2>
                <p className={styles.subtitle}>
                    Cada mascota merece una atención impeccable. Así es como AppCotas lo hace posible.
                </p>
            </div>

            <div className={styles.grid}>
                {pets.map((pet, index) => (
                    <motion.div
                        key={pet.id}
                        className={styles.card}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.06, duration: 0.4 }}
                        viewport={{ once: true }}
                    >
                        <div className={styles.cardImageWrap}>
                            <img
                                src={pet.image}
                                alt={pet.name}
                                className={styles.cardImage}
                                loading="lazy"
                            />
                            <div className={styles.cardOverlay} />
                            <span className={styles.cardTag}>
                                <SparklesIcon className="h-3 w-3" />
                                {pet.tag}
                            </span>
                        </div>
                        <div className={styles.cardContent}>
                            <h3 className={styles.cardTitle}>{pet.name}</h3>
                            <p className={styles.cardBreed}>{pet.breed}</p>
                            <p className={styles.cardStory}>{pet.story}</p>
                            <div className={styles.cardFooter}>
                                <CheckCircleIcon className={styles.footerIcon} />
                                <span className={styles.footerText}>Caso resuelto con AppCotas</span>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

export default PetStoriesSection;